import React, { useState } from 'react';
import {
  Card, Table, Button, Badge, Modal,
  Form, Tabs, Tab, Alert, Spinner, InputGroup,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaFileInvoiceDollar, FaEnvelope, FaCheck, FaUndo, FaTrash, FaEye, FaPlus, FaTimes } from 'react-icons/fa';
import Loader from '../../Components/Loader';
import Message from '../../Components/Message';
import BackButton from '../../Components/BackButton';
import {
  useGetEomSummaryQuery,
  useGetUserStatementsQuery,
  useGenerateStatementMutation,
  useSendStatementMutation,
  useRecordPaymentMutation,
  useUnrecordPaymentMutation,
  useDeleteStatementMutation,
  useUpdateStatementMutation,
  useSearchUserOrdersQuery,
  useFlagOrderAsEomMutation,
  useResendEomInvoiceMutation,
  useAddOrderToStatementMutation,
  useRemoveOrderFromStatementMutation,
} from '../../slices/eomApiSlice';

// Format YYYY-MM as "June 2026"
function formatMonth(month) {
  if (!month) return '—';
  try {
    return new Date(`${month}-01T12:00:00`).toLocaleString('en-CA', { month: 'long', year: 'numeric' });
  } catch {
    return month;
  }
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA');
}

function StatusBadge({ statement }) {
  if (statement.isPaid) return <Badge bg="success">Paid</Badge>;
  if (statement.dueDate && new Date(statement.dueDate) < new Date()) {
    return <Badge bg="danger">Overdue</Badge>;
  }
  return <Badge bg="warning" text="dark">Pending</Badge>;
}

// ── Add Order Modal ──────────────────────────────────────────────────────────
/**
 * mode = 'flag'             → flags order as EOM, it surfaces in Pending Orders
 * mode = 'add-to-statement' → adds order directly into an existing statement
 */
function AddOrderModal({ show, mode, userId, statementId, onHide, onDone }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedTerm, setSubmittedTerm] = useState('');

  const { data: results = [], isFetching } = useSearchUserOrdersQuery(
    { userId, q: submittedTerm },
    { skip: !submittedTerm || !userId }
  );

  const [flagOrderAsEom, { isLoading: isFlagging }] = useFlagOrderAsEomMutation();
  const [addOrderToStatement, { isLoading: isAdding }] = useAddOrderToStatementMutation();

  const isWorking = isFlagging || isAdding;

  const handleSearch = (e) => {
    e.preventDefault();
    setSubmittedTerm(searchTerm.trim());
  };

  const handleAdd = async (orderId) => {
    try {
      if (mode === 'flag') {
        await flagOrderAsEom(orderId).unwrap();
        toast.success('Order flagged as End of Month Payment — it will appear in Pending Orders');
      } else {
        await addOrderToStatement({ statementId, orderId }).unwrap();
        toast.success('Order added to statement');
      }
      onDone();
      onHide();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to add order');
    }
  };

  const handleHide = () => {
    setSearchTerm('');
    setSubmittedTerm('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'flag' ? 'Add Existing Order to EOM Pool' : 'Add Order to Statement'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Search by order reference or invoice number. Only unpaid orders not already in a statement are shown.
          {mode === 'flag'
            ? ' The order will be flagged as End of Month Payment and appear in Pending Orders.'
            : ' The order will be added directly to this statement and flagged as End of Month Payment.'}
        </p>
        <Form onSubmit={handleSearch} className="mb-3">
          <InputGroup>
            <Form.Control
              placeholder="Order ref or invoice # (e.g. AAC54E, INV-2026-001)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button type="submit" variant="outline-primary" disabled={!searchTerm.trim()}>
              {isFetching ? <Spinner size="sm" animation="border" /> : 'Search'}
            </Button>
          </InputGroup>
        </Form>

        {submittedTerm && !isFetching && results.length === 0 && (
          <Alert variant="warning">No eligible orders found for "{submittedTerm}".</Alert>
        )}

        {results.length > 0 && (
          <Table striped hover responsive size="sm">
            <thead>
              <tr>
                <th>Date</th>
                <th>Order Ref</th>
                <th>Invoice #</th>
                <th>Payment Method</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {results.map((o) => (
                <tr key={o._id}>
                  <td>{formatDate(o.createdAt)}</td>
                  <td>{o.reference || '—'}</td>
                  <td>{o.invoice || '—'}</td>
                  <td>
                    {o.paymentMethod !== 'End of Month Payment' ? (
                      <Badge bg="warning" text="dark" title="Payment method will be changed to EOM">
                        {o.paymentMethod}
                      </Badge>
                    ) : (
                      <Badge bg="success">EOM</Badge>
                    )}
                  </td>
                  <td>${Number(o.totalPrice || 0).toFixed(2)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleAdd(o._id)}
                      disabled={isWorking}
                    >
                      <FaPlus />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Generate Statement Modal ─────────────────────────────────────────────────
function GenerateModal({ show, userId, userName, pendingOrders, onHide, onGenerated }) {
  const prevMonth = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();

  const [month, setMonth] = useState(prevMonth);
  const [conflict, setConflict] = useState(null); // { existing, incoming } when 409
  const [generateStatement, { isLoading }] = useGenerateStatementMutation();

  const handleHide = () => {
    setConflict(null);
    onHide();
  };

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
    setConflict(null); // clear any previous conflict when month changes
  };

  const doGenerate = async (force = false) => {
    try {
      await generateStatement({ userId, month, force }).unwrap();
      toast.success(`Statement ${force ? 'regenerated' : 'generated'} for ${formatMonth(month)}`);
      setConflict(null);
      onGenerated();
      handleHide();
    } catch (err) {
      if (err?.status === 409) {
        setConflict(err.data); // { existing, incoming }
      } else {
        toast.error(err?.data?.message || 'Failed to generate statement');
      }
    }
  };

  const ordersInMonth = pendingOrders?.filter((o) => {
    if (!o.createdAt || !month) return false;
    const om = new Date(o.createdAt);
    const key = `${om.getFullYear()}-${String(om.getMonth() + 1).padStart(2, '0')}`;
    return key === month;
  }) || [];

  return (
    <Modal show={show} onHide={handleHide} size={conflict ? 'lg' : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>Generate Statement — {userName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!conflict ? (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Month (YYYY-MM)</Form.Label>
              <Form.Control
                type="month"
                value={month}
                onChange={handleMonthChange}
              />
            </Form.Group>
            {month && (
              <Alert variant={ordersInMonth.length > 0 ? 'info' : 'warning'}>
                {ordersInMonth.length > 0
                  ? `${ordersInMonth.length} pending order(s) found for ${formatMonth(month)}.`
                  : `No pending orders found for ${formatMonth(month)}.`}
              </Alert>
            )}
          </>
        ) : (
          <>
            <Alert variant="warning">
              <strong>A statement already exists for {formatMonth(month)}.</strong>{' '}
              Regenerating will replace its order list with the current Pending Orders for that month.
              Manually added orders will be removed. This cannot be undone.
            </Alert>
            <div className="row">
              <div className="col-md-6">
                <p className="fw-semibold mb-1 text-danger">
                  Current statement ({(conflict.existing?.orders || []).length} order(s) — ${Number(conflict.existing?.total || 0).toFixed(2)})
                </p>
                <Table size="sm" striped bordered>
                  <thead><tr><th>Ref</th><th>Invoice</th><th className="text-end">Amount</th></tr></thead>
                  <tbody>
                    {(conflict.existing?.orders || []).map((o) => (
                      <tr key={String(o.orderId)}>
                        <td>{o.orderRef || '—'}</td>
                        <td>{o.invoice || '—'}</td>
                        <td className="text-end">${Number(o.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="col-md-6">
                <p className="fw-semibold mb-1 text-success">
                  Incoming ({(conflict.incoming || []).length} order(s) — ${(conflict.incoming || []).reduce((s, o) => s + o.total, 0).toFixed(2)})
                </p>
                <Table size="sm" striped bordered>
                  <thead><tr><th>Ref</th><th>Invoice</th><th className="text-end">Amount</th></tr></thead>
                  <tbody>
                    {(conflict.incoming || []).map((o) => (
                      <tr key={String(o.orderId)}>
                        <td>{o.orderRef || '—'}</td>
                        <td>{o.invoice || '—'}</td>
                        <td className="text-end">${Number(o.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide}>Cancel</Button>
        {!conflict ? (
          <Button variant="primary" onClick={() => doGenerate(false)} disabled={isLoading || !month}>
            {isLoading ? <Spinner size="sm" animation="border" /> : 'Generate'}
          </Button>
        ) : (
          <Button variant="danger" onClick={() => doGenerate(true)} disabled={isLoading}>
            {isLoading ? <Spinner size="sm" animation="border" /> : 'Confirm & Regenerate'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ── Record Payment Modal ─────────────────────────────────────────────────────
function PaymentModal({ show, statement, onHide, onPaid }) {
  const [paymentMethod, setPaymentMethod] = useState('cheque');
  const [paymentNote, setPaymentNote] = useState('');
  const [paidAmount, setPaidAmount] = useState('');
  const [recordPayment, { isLoading }] = useRecordPaymentMutation();

  const amount = paidAmount || String(statement?.total?.toFixed(2) || '0.00');

  const handlePay = async () => {
    try {
      await recordPayment({
        statementId: statement._id,
        paymentMethod,
        paymentNote,
        paidAmount: Number(amount),
      }).unwrap();
      toast.success('Payment recorded');
      onPaid();
      onHide();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to record payment');
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Record Payment — {statement?.statementNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Amount (CAD)</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setPaidAmount(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Payment Method</Form.Label>
          <Form.Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <option value="cheque">Cheque</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="etransfer">E-Transfer</option>
            <option value="other">Other</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Notes (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
            placeholder="Cheque #, reference, etc."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="success" onClick={handlePay} disabled={isLoading}>
          {isLoading ? <Spinner size="sm" animation="border" /> : 'Record Payment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Customer Detail Panel ─────────────────────────────────────────────────────
function CustomerDetail({ userId, onClose }) {
  const { data, isLoading, error, refetch } = useGetUserStatementsQuery(userId, {
    refetchOnMountOrArgChange: true,
  });

  const [sendStatement, { isLoading: isSending }] = useSendStatementMutation();
  const [unrecordPayment] = useUnrecordPaymentMutation();
  const [deleteStatement] = useDeleteStatementMutation();
  const [updateStatement] = useUpdateStatementMutation();
  const [removeOrderFromStatement] = useRemoveOrderFromStatementMutation();
  const [resendEomInvoice] = useResendEomInvoiceMutation();

  const [generateModal, setGenerateModal] = useState(false);
  const [payModal, setPayModal] = useState(null); // statement object
  const [dueDateEditing, setDueDateEditing] = useState({}); // { [id]: value }
  const [addOrderModal, setAddOrderModal] = useState(null); // null | { mode, statementId? }

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || 'Error loading data'}</Message>;

  const { user, statements = [], pendingOrders = [] } = data || {};

  const handleSend = async (statementId) => {
    try {
      await sendStatement(statementId).unwrap();
      toast.success('Statement emailed to customer');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to send statement');
    }
  };

  const handleUnpay = async (statementId) => {
    if (!window.confirm('Reverse this payment? Orders will be marked unpaid.')) return;
    try {
      await unrecordPayment(statementId).unwrap();
      toast.success('Payment reversed');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to reverse payment');
    }
  };

  const handleDelete = async (statementId) => {
    if (!window.confirm('Delete this statement? This cannot be undone.')) return;
    try {
      await deleteStatement(statementId).unwrap();
      toast.success('Statement deleted');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete statement');
    }
  };

  const handleDueDateSave = async (statementId, value) => {
    try {
      await updateStatement({ statementId, dueDate: value || null }).unwrap();
      setDueDateEditing((prev) => ({ ...prev, [statementId]: undefined }));
      toast.success('Due date updated');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update due date');
    }
  };

  const handleRemoveOrder = async (statementId, orderId) => {
    if (!window.confirm('Remove this order from the statement? It will return to the Pending Orders pool.')) return;
    try {
      await removeOrderFromStatement({ statementId, orderId }).unwrap();
      toast.success('Order removed from statement');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to remove order');
    }
  };

  const handleResendInvoice = async (orderId) => {
    try {
      await resendEomInvoice(orderId).unwrap();
      toast.success('Invoice email resent');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to resend invoice');
    }
  };

  const handleViewStatementPdf = async (statementId) => {
    try {
      const res = await fetch(`/api/eom/statements/${statementId}/pdf`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Could not load statement PDF');
    }
  };

  return (
    <Card className="mt-4 shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{user?.name}</strong>
          <span className="text-muted ms-2">{user?.email}</span>
          <Badge bg="info" className="ms-2">EOM Account</Badge>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>Close</Button>
      </Card.Header>

      <Card.Body>
        <Tabs defaultActiveKey="statements" className="mb-3">

          {/* ── Tab 1: Statements ── */}
          <Tab eventKey="statements" title={`Statements (${statements.length})`}>
            <div className="d-flex justify-content-end gap-2 mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setAddOrderModal({ mode: 'flag' })}
              >
                <FaPlus className="me-1" /> Add Order to EOM Pool
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setGenerateModal(true)}
              >
                <FaFileInvoiceDollar className="me-1" /> Generate Statement
              </Button>
            </div>

            {statements.length === 0 ? (
              <Alert variant="light" className="border">No statements yet.</Alert>
            ) : (
              <Table striped hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Statement #</th>
                    <th>Month</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Due Date</th>
                    <th>Sent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((stmt) => {
                    const editingDue = dueDateEditing[stmt._id];
                    return (
                      <React.Fragment key={stmt._id}>
                      <tr>
                        <td>{stmt.statementNumber || '—'}</td>
                        <td>{formatMonth(stmt.month)}</td>
                        <td>${Number(stmt.total || 0).toFixed(2)}</td>
                        <td><StatusBadge statement={stmt} /></td>
                        <td>
                          {editingDue !== undefined ? (
                            <div className="d-flex gap-1">
                              <Form.Control
                                type="date"
                                size="sm"
                                style={{ width: 130 }}
                                value={editingDue}
                                onChange={(e) =>
                                  setDueDateEditing((prev) => ({ ...prev, [stmt._id]: e.target.value }))
                                }
                              />
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleDueDateSave(stmt._id, editingDue)}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() =>
                                  setDueDateEditing((prev) => { const n = {...prev}; delete n[stmt._id]; return n; })
                                }
                              >
                                ✕
                              </Button>
                            </div>
                          ) : (
                            <span
                              role="button"
                              className="text-decoration-underline"
                              style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                              onClick={() =>
                                setDueDateEditing((prev) => ({
                                  ...prev,
                                  [stmt._id]: stmt.dueDate
                                    ? new Date(stmt.dueDate).toISOString().slice(0, 10)
                                    : '',
                                }))
                              }
                            >
                              {stmt.dueDate ? formatDate(stmt.dueDate) : <span className="text-muted">Set date</span>}
                            </span>
                          )}
                        </td>
                        <td>{stmt.sentAt ? formatDate(stmt.sentAt) : <span className="text-muted">Not sent</span>}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => handleViewStatementPdf(stmt._id)}
                              title="Download PDF"
                            >
                              PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleSend(stmt._id)}
                              disabled={isSending}
                              title="Email statement to customer"
                            >
                              <FaEnvelope />
                            </Button>
                            {!stmt.isPaid && (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setAddOrderModal({ mode: 'add-to-statement', statementId: stmt._id })}
                                title="Add order to this statement"
                              >
                                <FaPlus />
                              </Button>
                            )}
                            {!stmt.isPaid && (
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => setPayModal(stmt)}
                                title="Record payment"
                              >
                                <FaCheck />
                              </Button>
                            )}
                            {stmt.isPaid && (
                              <Button
                                size="sm"
                                variant="outline-warning"
                                onClick={() => handleUnpay(stmt._id)}
                                title="Reverse payment"
                              >
                                <FaUndo />
                              </Button>
                            )}
                            {!stmt.isPaid && (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDelete(stmt._id)}
                                title="Delete statement"
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Order line items within the statement */}
                      {(stmt.orders || []).length > 0 && (
                        <tr key={`${stmt._id}-orders`}>
                          <td colSpan={7} className="p-0 bg-light">
                            <Table size="sm" className="mb-0 ms-3" style={{ width: 'calc(100% - 1rem)' }}>
                              <tbody>
                                {stmt.orders.map((o) => (
                                  <tr key={o.orderId} className="text-muted small">
                                    <td style={{ width: 100 }}>{formatDate(o.createdAt)}</td>
                                    <td>{o.orderRef || '—'}</td>
                                    <td>{o.invoice || '—'}</td>
                                    <td className="text-end">${Number(o.total || 0).toFixed(2)}</td>
                                    <td style={{ width: 40 }}>
                                      {!stmt.isPaid && (
                                        <Button
                                          size="sm"
                                          variant="link"
                                          className="text-danger p-0"
                                          title="Remove order from statement"
                                          onClick={() => handleRemoveOrder(stmt._id, o.orderId)}
                                        >
                                          <FaTimes />
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Tab>

          {/* ── Tab 2: Pending Orders ── */}
          <Tab eventKey="pending" title={`Pending Orders (${pendingOrders.length})`}>
            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setAddOrderModal({ mode: 'flag' })}
              >
                <FaPlus className="me-1" /> Add Existing Order
              </Button>
            </div>
            {pendingOrders.length === 0 ? (
              <Alert variant="light" className="border">
                No pending orders outside of a statement.
              </Alert>
            ) : (
              <>
                <p className="text-muted small">
                  These orders are not yet included in a statement.
                  Generate a statement to include them.
                </p>
                <Table striped hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Order Ref</th>
                      <th>Invoice #</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingOrders.map((o) => (
                      <tr key={o._id}>
                        <td>{formatDate(o.createdAt)}</td>
                        <td>{o.reference || '—'}</td>
                        <td>{o.invoice || '—'}</td>
                        <td>${Number(o.totalPrice || 0).toFixed(2)}</td>
                        <td>
                          {o.invoice && (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              title="Resend invoice email"
                              onClick={() => handleResendInvoice(o._id)}
                            >
                              <FaEnvelope />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="text-end mt-2">
                  <strong>
                    Total Pending: $
                    {pendingOrders
                      .reduce((s, o) => s + Number(o.totalPrice || 0), 0)
                      .toFixed(2)}
                  </strong>
                </div>
              </>
            )}
          </Tab>
        </Tabs>
      </Card.Body>

      {/* Modals */}
      <GenerateModal
        show={generateModal}
        userId={userId}
        userName={user?.name}
        pendingOrders={pendingOrders}
        onHide={() => setGenerateModal(false)}
        onGenerated={refetch}
      />

      {payModal && (
        <PaymentModal
          show={!!payModal}
          statement={payModal}
          onHide={() => setPayModal(null)}
          onPaid={refetch}
        />
      )}

      {addOrderModal && (
        <AddOrderModal
          show={!!addOrderModal}
          mode={addOrderModal.mode}
          userId={userId}
          statementId={addOrderModal.statementId}
          onHide={() => setAddOrderModal(null)}
          onDone={refetch}
        />
      )}
    </Card>
  );
}

// ── Main Screen ───────────────────────────────────────────────────────────────
const EomBillingScreen = () => {
  const { data: summary, isLoading, error, refetch } = useGetEomSummaryQuery();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [quickGenerate, setQuickGenerate] = useState(null); // { userId, userName }

  return (
    <>
      <BackButton />
      <h1 className="mb-4">EOM Billing</h1>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'Error loading summary'}</Message>
      ) : (
        <>
          {/* ── Summary Table ── */}
          <Card className="shadow-sm">
            <Card.Header>
              <strong>End-of-Month Accounts</strong>
              <span className="text-muted ms-2 small">({summary?.length || 0} customers)</span>
            </Card.Header>
            <Card.Body className="p-0">
              {!summary?.length ? (
                <Alert variant="light" className="m-3 border">
                  No customers have End of Month Payment enabled. Enable it in the User Edit screen.
                </Alert>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Customer</th>
                      <th>Email</th>
                      <th>Pending Orders</th>
                      <th>Outstanding</th>
                      <th>Last Statement</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((row) => (
                      <tr
                        key={row.userId}
                        className={selectedUserId === row.userId ? 'table-active' : ''}
                      >
                        <td>{row.name}</td>
                        <td className="text-muted">{row.email}</td>
                        <td>{row.pendingOrderCount}</td>
                        <td>
                          <span
                            className={row.pendingOrderTotal > 0 ? 'text-danger fw-bold' : 'text-success'}
                          >
                            ${Number(row.pendingOrderTotal || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          {row.lastStatement ? (
                            <>
                              {row.lastStatement.statementNumber}{' '}
                              <StatusBadge statement={row.lastStatement} />
                            </>
                          ) : (
                            <span className="text-muted">None</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant={selectedUserId === row.userId ? 'primary' : 'outline-primary'}
                              onClick={() =>
                                setSelectedUserId(
                                  selectedUserId === row.userId ? null : row.userId
                                )
                              }
                              title="View customer detail"
                            >
                              <FaEye />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() =>
                                setQuickGenerate({ userId: row.userId, userName: row.name })
                              }
                              title="Generate statement"
                            >
                              <FaFileInvoiceDollar />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* ── Customer Detail ── */}
          {selectedUserId && (
            <CustomerDetail
              key={selectedUserId}
              userId={selectedUserId}
              onClose={() => setSelectedUserId(null)}
            />
          )}
        </>
      )}

      {/* Quick Generate from summary row */}
      {quickGenerate && (
        <GenerateModal
          show={!!quickGenerate}
          userId={quickGenerate.userId}
          userName={quickGenerate.userName}
          pendingOrders={[]}
          onHide={() => setQuickGenerate(null)}
          onGenerated={() => { refetch(); if (selectedUserId === quickGenerate.userId) {} }}
        />
      )}
    </>
  );
};

export default EomBillingScreen;
